import { Asset, AssetDependency } from '@/api/generatedApi';
import { useGetAssetDependencies } from '@/hooks/api/assets/useGetAssetDependencies';
import { getFilename } from '@/utils/path';
import { Box, Skeleton, SxProps, Tooltip, IconButton, CircularProgress } from '@mui/material';
import { createUseStyles } from '@/services/createUseStyles';
import { SimpleTreeView, TreeItem } from '@mui/x-tree-view';
import { Upload } from '@mui/icons-material';
import { FileInputs } from '@/components/Upload/FileInputs/FileInputs';
import { UploadDrawer } from '@/components/Upload/UploadDrawer/UploadDrawer';
import { useDependencyUpload } from '@/hooks/useDependencyUpload';
import React, { ReactNode, useRef } from 'react';
import { UploadTestIds } from '@/enums/uploadTestIds.enum';
import { MissingDependencyIcon } from '@/components/MissingDependencyIcon/MissingDependencyIcon';
import { useInfiniteScroll } from '@/hooks/useInfiniteScroll';

const useStyles = createUseStyles(() => ({
  DependencyTreeView_LoadState: {
    marginLeft: '15px',
    height: '32px',
  },
  DependencyTreeView_TreeView: {
    paddingLeft: '.5rem',
    '& .MuiTreeItem-iconContainer': {
      '& svg': {
        fontSize: '1.25rem',
      },
    },
  },
  TreeItemWrapper_label: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    padding: '10px 0',
    height: '1.5rem',
  },
  TreeItemWrapper_text: {
    display: 'flex',
    alignItems: 'center',
    flex: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    minWidth: 0,
    fontSize: '0.875rem',
  },
  TreeItemWrapper_uploadButton: {
    padding: '4px',
    marginLeft: '8px',
    '& .MuiSvgIcon-root': {
      fontSize: '1rem',
    },
  },
  TreeItemWrapper_filename: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    display: 'inline-block',
    maxWidth: 'calc(100% - 20px)',
  },
  TreeItemWrapper_missingIcon: {
    marginLeft: '6px',
    display: 'flex',
  },
  TreeItemWrapper_rootMissingIcon: {
    cursor: 'default',
    marginRight: '4px',
  },
}));

interface LabelWrapperProperties {
  children: React.ReactElement;
  isMissing?: boolean;
  isMissingTooltip?: string;
}

const LabelWrapper = ({ children, isMissing, isMissingTooltip }: LabelWrapperProperties) => {
  return isMissing ? <Tooltip title={isMissingTooltip}>{children}</Tooltip> : children;
};

interface TreeItemWrapperProperties {
  id: string;
  path: string;
  children?: ReactNode;
  sx?: SxProps;
  isMissing?: boolean;
  onClick?: () => void;
  onUploadClick?: (path: string) => void;
  isUploading?: boolean;
  hasMissingChildDependencies?: boolean;
}

export const TreeItemWrapper = ({
  id,
  path,
  sx,
  children,
  isMissing,
  onClick,
  onUploadClick,
  isUploading = false,
  hasMissingChildDependencies = false,
}: TreeItemWrapperProperties) => {
  const c = useStyles();
  const shouldShowUploadButton = isMissing && onUploadClick;

  return (
    <TreeItem
      itemId={id}
      label={
        <LabelWrapper
          isMissing={isMissing}
          isMissingTooltip={`Missing dependency. This file can be uploaded to "${path}" to resolve.`}
        >
          <Box
            className={c.TreeItemWrapper_label}
            onClick={event => {
              event.stopPropagation();
              onClick?.();
            }}
            gap={1}
            style={{ cursor: onClick ? 'pointer' : undefined }}
          >
            <Box className={c.TreeItemWrapper_text}>
              <span className={c.TreeItemWrapper_filename}>{getFilename(path)}</span>
              {isMissing && (
                <span className={c.TreeItemWrapper_missingIcon} data-testid='MissingDependenciesIcon'>
                  <MissingDependencyIcon hideTooltip />
                </span>
              )}
            </Box>
            {hasMissingChildDependencies && (
              <Box className={c.TreeItemWrapper_rootMissingIcon} data-testid='MissingAssetIcon'>
                <MissingDependencyIcon />
              </Box>
            )}
            {shouldShowUploadButton &&
              (isUploading ? (
                <IconButton size='small' disabled>
                  <CircularProgress size={16} />
                </IconButton>
              ) : (
                <IconButton
                  data-testid={UploadTestIds.UploadDependencyButton}
                  className={c.TreeItemWrapper_uploadButton}
                  onClick={event => {
                    event.stopPropagation();
                    onUploadClick(path);
                  }}
                  size='small'
                  color='primary'
                  aria-label={`Upload file for ${getFilename(path)}`}
                >
                  <Upload />
                </IconButton>
              ))}
          </Box>
        </LabelWrapper>
      }
      sx={sx}
    >
      {children}
    </TreeItem>
  );
};

const getUniqueItemId = (item: AssetDependency, parentPath: string = ''): string => {
  const baseId = item.asset?.id ? `${item.asset.id}-${item.path}` : `missing-${item.path}`;
  return parentPath ? `${parentPath}/${baseId}` : baseId;
};

interface DependencyTreeItemProperties {
  item: AssetDependency;
  parentPath?: string;
  level?: number;
  onTreeItemClick?: (itemId: string) => void;
  onUploadClick?: (path: string) => void;
  uploadingPaths?: Set<string>;
}

const DependencyTreeItem = ({
  item,
  parentPath,
  level = 0,
  onTreeItemClick,
  onUploadClick,
  uploadingPaths = new Set(),
}: DependencyTreeItemProperties) => {
  const c = useStyles();
  const itemReference = useRef<HTMLDivElement>(null);
  const { dependencies, error, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } = useGetAssetDependencies({
    assetId: item.asset?.id,
    enabled: item.hasDependencies,
  });

  useInfiniteScroll(itemReference, {
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  });

  if (error) {
    return;
  }

  if (isLoading) {
    return (
      <Skeleton
        data-testid='dependency-tree-item-skeleton'
        width={180}
        className={c.DependencyTreeView_LoadState}
        variant='text'
      />
    );
  }

  const currentItemId = getUniqueItemId(item, parentPath);
  const baseLevel = level + 1;
  const hasMissingChildDependencies =
    (item.asset?.isMissingDependencies ?? false) ||
    (dependencies?.some(dep => !dep.asset || dep.asset?.isMissingDependencies) ?? false);

  return (
    <div ref={itemReference}>
      <TreeItemWrapper
        id={currentItemId}
        path={item.path}
        sx={{
          '& .MuiTreeItem-content': {
            paddingLeft:
              (dependencies || []).length > 0
                ? `${baseLevel * 0.5}rem !important`
                : `${baseLevel * 1.25}rem !important`,
          },
        }}
        onClick={() => onTreeItemClick?.(item.asset?.id ?? '')}
        isMissing={!item.asset}
        onUploadClick={onUploadClick}
        isUploading={uploadingPaths.has(item.path)}
        hasMissingChildDependencies={hasMissingChildDependencies}
      >
        {dependencies?.map(dependency => {
          const currentDependencyItemId = `${currentItemId}/${getUniqueItemId(dependency)}`;
          return dependency.hasDependencies && dependency.asset ? (
            <DependencyTreeItem
              key={currentDependencyItemId}
              item={dependency}
              parentPath={currentItemId}
              level={baseLevel}
              onTreeItemClick={onTreeItemClick}
              onUploadClick={onUploadClick}
              uploadingPaths={uploadingPaths}
            />
          ) : (
            <TreeItemWrapper
              key={currentDependencyItemId}
              id={currentDependencyItemId}
              path={dependency.path}
              sx={{
                '& .MuiTreeItem-content': {
                  paddingLeft: `${baseLevel + 1}rem !important`,
                },
              }}
              onClick={() => onTreeItemClick?.(dependency.asset?.id ?? '')}
              isMissing={!dependency.asset}
              onUploadClick={onUploadClick}
              isUploading={uploadingPaths.has(dependency.path)}
              hasMissingChildDependencies={dependency.asset?.isMissingDependencies ?? false}
            />
          );
        })}
        {isFetchingNextPage && (
          <Box className={c.DependencyTreeView_LoadState}>
            <Skeleton width={180} variant='text' />
          </Box>
        )}
      </TreeItemWrapper>
    </div>
  );
};

export interface DependencyTreeViewProperties {
  asset: Asset;
  onTreeItemClick?: (itemId: string) => void;
}

export const DependencyTreeView = ({ asset, onTreeItemClick }: DependencyTreeViewProperties) => {
  const treeViewReference = useRef<HTMLDivElement>(null);
  const { dependencies, error, fetchNextPage, hasNextPage, isFetchingNextPage } = useGetAssetDependencies({
    assetId: asset.id,
  });

  useInfiniteScroll(treeViewReference, {
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  });

  const {
    fileInputReference,
    folderInputReference,
    files,
    uploadProgress,
    drawerOpen,
    handleDrawerClose,
    uploadingPaths,
    handleUploadClick,
    fileInputProps,
  } = useDependencyUpload();

  const c = useStyles();

  if (!dependencies || error) {
    return;
  }

  return (
    <>
      <div ref={treeViewReference}>
        <SimpleTreeView
          key={asset.id}
          aria-label='dependency tree'
          data-testid='dependency-tree'
          className={c.DependencyTreeView_TreeView}
        >
          {dependencies.map(dependency => {
            const currentItemId = getUniqueItemId(dependency);
            return dependency.hasDependencies && dependency.asset ? (
              <DependencyTreeItem
                key={currentItemId}
                item={dependency}
                level={0}
                onTreeItemClick={onTreeItemClick}
                onUploadClick={handleUploadClick}
                uploadingPaths={uploadingPaths}
              />
            ) : (
              <TreeItemWrapper
                key={currentItemId}
                id={currentItemId}
                path={dependency.path}
                onClick={() => onTreeItemClick?.(dependency.asset?.id ?? '')}
                isMissing={!dependency.asset}
                onUploadClick={handleUploadClick}
                isUploading={uploadingPaths.has(dependency.path)}
                hasMissingChildDependencies={dependency.asset?.isMissingDependencies ?? false}
              />
            );
          })}
          {isFetchingNextPage && (
            <Box className={c.DependencyTreeView_LoadState}>
              <Skeleton width={180} variant='text' />
            </Box>
          )}
        </SimpleTreeView>
      </div>
      <FileInputs
        fileInputProps={fileInputProps}
        fileInputReference={fileInputReference}
        folderInputReference={folderInputReference}
      />
      <UploadDrawer files={files} uploadProgress={uploadProgress} isOpen={drawerOpen} onClose={handleDrawerClose} />
    </>
  );
};
